import { useState, useEffect, useCallback, useRef } from 'react';
import { libraryAPI } from '@/lib/api/library';
import type { BulkLibrary } from '@genieartinc/schemas';

// Module-level cache so library IDs are fetched once across all component instances
let cachedIds: Set<string> | null = null;
let fetchPromise: Promise<void> | null = null;
// Every mounted hook instance, so a change in one panel (Library) shows in another (node settings)
const listeners = new Set<(ids: Set<string>) => void>();

function publish(next: Set<string>): void {
  cachedIds = next;
  listeners.forEach((listener) => listener(next));
  if (typeof document !== 'undefined') document.dispatchEvent(new CustomEvent('genie:library-changed'));
}

function clearCache(): void {
  cachedIds = null;
  fetchPromise = null;
}

interface UseLibraryReturn {
  libraryIds: Set<string>;
  isInLibrary: (modelId: string) => boolean;
  addToLibrary: (modelId: string) => Promise<void>;
  removeFromLibrary: (modelId: string) => Promise<void>;
  toggleLibrary: (modelId: string) => Promise<void>;
  /** Add/remove many models (ids, or every enabled model matching a catalog filter). Resolves to the affected count. */
  bulkUpdate: (body: BulkLibrary) => Promise<number>;
  isLoading: boolean;
}

export function useLibrary(enabled = true): UseLibraryReturn {
  const [libraryIds, setLibraryIds] = useState<Set<string>>(cachedIds ?? new Set());
  const [isLoading, setIsLoading] = useState(cachedIds === null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    listeners.add(setLibraryIds);
    return () => {
      mountedRef.current = false;
      listeners.delete(setLibraryIds);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (cachedIds !== null) {
      setLibraryIds(cachedIds);
      setIsLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = libraryAPI.getLibraryModelIds().then((ids) => {
        cachedIds = new Set(ids);
        fetchPromise = null;
      }).catch(() => {
        fetchPromise = null;
        cachedIds = new Set();
      });
    }

    fetchPromise.then(() => {
      if (mountedRef.current) {
        setLibraryIds(cachedIds ?? new Set());
        setIsLoading(false);
      }
    });
  }, [enabled]);

  const isInLibrary = useCallback((modelId: string) => libraryIds.has(modelId), [libraryIds]);

  const addToLibrary = useCallback(async (modelId: string) => {
    // Optimistic update
    const prev = new Set(libraryIds);
    const next = new Set(libraryIds);
    next.add(modelId);
    publish(next);

    try {
      await libraryAPI.addToLibrary(modelId);
    } catch {
      // Revert on error
      publish(prev);
    }
  }, [libraryIds]);

  const removeFromLibrary = useCallback(async (modelId: string) => {
    // Optimistic update
    const prev = new Set(libraryIds);
    const next = new Set(libraryIds);
    next.delete(modelId);
    publish(next);

    try {
      await libraryAPI.removeFromLibrary(modelId);
    } catch {
      // Revert on error
      publish(prev);
    }
  }, [libraryIds]);

  const toggleLibrary = useCallback(async (modelId: string) => {
    if (libraryIds.has(modelId)) {
      await removeFromLibrary(modelId);
    } else {
      await addToLibrary(modelId);
    }
  }, [libraryIds, addToLibrary, removeFromLibrary]);

  const bulkUpdate = useCallback(async (body: BulkLibrary) => {
    // Not optimistic: a filter can match hundreds of models the client has not loaded
    const ids = await libraryAPI.bulkUpdate(body);
    const next = new Set(cachedIds ?? libraryIds);
    for (const id of ids) {
      if (body.action === 'add') next.add(id);
      else next.delete(id);
    }
    publish(next);
    return ids.length;
  }, [libraryIds]);

  return { libraryIds, isInLibrary, addToLibrary, removeFromLibrary, toggleLibrary, bulkUpdate, isLoading: enabled && isLoading };
}

// Export cache-clearing utility for testing and logout
export { clearCache as clearLibraryCache };
